import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import ReviewsBar from "../components/ReviewsBar";
import "../styles/parkingVsTaxi.css";

const LHR_API = {
  flightSuggest: "/api/heathrow/flights/suggest",
  flightSchedules: "/api/heathrow/flights/schedules",
};

const flightNumKey = n => String(n || "").replace(/\s+/g, "").toUpperCase();
const isLikelyFlightNumber = value => /^[A-Z0-9]{2,3}\d{1,4}$/.test(flightNumKey(value));
const scheduleAnchorTime = s =>
  s?.anchor_time_utc || s?.estimated_time_utc || s?.scheduled_anchor_utc || s?.scheduled_time_utc || null;
const formatScheduleLabel = iso => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Europe/London",
  });
};
const scheduleDateKey = s => {
  const anchor = scheduleAnchorTime(s);
  if (!anchor) return String(s?.date || s?.snapshot_date || "").slice(0, 10);
  const d = new Date(anchor);
  if (Number.isNaN(d.getTime())) return String(s?.date || s?.snapshot_date || "").slice(0, 10);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const year = parts.find(p => p.type === "year")?.value;
  const month = parts.find(p => p.type === "month")?.value;
  const day = parts.find(p => p.type === "day")?.value;
  return year && month && day ? `${year}-${month}-${day}` : String(anchor).slice(0, 10);
};
const dedupeSchedules = (items = [], directionLabel = "Arrival") => {
  const uniq = new Map();
  (Array.isArray(items) ? items : []).forEach(s => {
    const anchor = scheduleAnchorTime(s);
    if (!anchor) return;
    const dateKey = scheduleDateKey(s) || String(anchor).slice(0, 10);
    const key = `${directionLabel}|${dateKey}|${anchor}`;
    if (!uniq.has(key)) uniq.set(key, s);
  });
  return Array.from(uniq.values()).sort(
    (a, b) => new Date(scheduleAnchorTime(a)).getTime() - new Date(scheduleAnchorTime(b)).getTime()
  );
};
const mapHeathrowSchedulesToBuckets = payload => {
  const rows = Array.isArray(payload?.schedules) ? payload.schedules : [];
  return {
    Arrival: rows.filter(r => String(r?.direction || "").toLowerCase() === "arrival"),
    Departure: rows.filter(r => String(r?.direction || "").toLowerCase() === "departure"),
  };
};

const todayUpdatedLabel = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "Europe/London",
}).format(new Date());

const LeaveForHeathrow = () => {
  const [pickupPostcode, setPickupPostcode] = useState("SW6 5HJ");
  const [flightNumber, setFlightNumber] = useState("BA566");
  const [passengers, setPassengers] = useState(2);
  const [bags, setBags] = useState(2);
  const [hasCheckedBags, setHasCheckedBags] = useState(true);
  const [onlineCheckin, setOnlineCheckin] = useState(true);
  const [transportMode, setTransportMode] = useState("taxi");
  const [riskProfile, setRiskProfile] = useState("family_comfort");

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [flightSuggestions, setFlightSuggestions] = useState([]);
  const [flightFocused, setFlightFocused] = useState(false);
  const [flightActiveIdx, setFlightActiveIdx] = useState(-1);
  const [flightError, setFlightError] = useState("");
  const [flightSchedules, setFlightSchedules] = useState({ Arrival: [], Departure: [] });
  const [flightDates, setFlightDates] = useState([]);
  const [flightDirection, setFlightDirection] = useState("Arrival");
  const [flightDate, setFlightDate] = useState("");
  const [flightAnchorUtc, setFlightAnchorUtc] = useState("");
  const [flightDateError, setFlightDateError] = useState("");
  const flightTimer = useRef(null);
  const flightScheduleTimer = useRef(null);
  const flightScheduleRequestSeq = useRef(0);
  const flightListboxId = "leave-heathrow-flight-suggestions";

  const availableFlightDirections = [
    ...(flightSchedules.Arrival.length ? ["Arrival"] : []),
    ...(flightSchedules.Departure.length ? ["Departure"] : []),
  ];
  const resolvedFlightDirection = availableFlightDirections.includes(flightDirection)
    ? flightDirection
    : availableFlightDirections[0] || "Arrival";
  const shouldShowFlightDirection = availableFlightDirections.length > 1;

  const fetchFlightSuggestions = value => {
    const v = flightNumKey(value);
    if (flightTimer.current) clearTimeout(flightTimer.current);
    if (v.length < 2) {
      setFlightSuggestions([]);
      setFlightActiveIdx(-1);
      setFlightError(v.length === 0 ? "" : "Flight number format looks invalid.");
      return;
    }
    if (!isLikelyFlightNumber(v)) {
      setFlightError("Use flight format like BA566 or KU103.");
    } else {
      setFlightError("");
    }
    flightTimer.current = setTimeout(async () => {
      try {
        const resp = await api.get(LHR_API.flightSuggest, { params: { q: v, limit: 8 } });
        const list = resp.data?.suggestions || [];
        setFlightSuggestions(list.map(f => ({ flightNumber: f })));
        setFlightActiveIdx(-1);
      } catch {
        setFlightSuggestions([]);
        setFlightActiveIdx(-1);
      }
    }, 180);
  };

  const refreshFlightSchedules = (flightNum, { debounce = true, resetSelection = true, preferredDirection = null } = {}) => {
    const clean = flightNumKey(flightNum);
    if (flightScheduleTimer.current) clearTimeout(flightScheduleTimer.current);
    if (!clean || clean.length < 2) {
      setFlightSchedules({ Arrival: [], Departure: [] });
      setFlightDates([]);
      setFlightDateError("");
      if (resetSelection) {
        setFlightDirection("Arrival");
        setFlightDate("");
        setFlightAnchorUtc("");
      }
      return;
    }

    const runner = async () => {
      const requestId = ++flightScheduleRequestSeq.current;
      try {
        const lookupResp = await api.get(LHR_API.flightSchedules, {
          params: { flight_number: clean, past_hours: 24, future_days: 10 },
        });
        if (requestId !== flightScheduleRequestSeq.current) return;
        const mapped = mapHeathrowSchedulesToBuckets(lookupResp?.data || null);
        const arrival = dedupeSchedules(mapped.Arrival || [], "Arrival");
        const departure = dedupeSchedules(mapped.Departure || [], "Departure");
        const options = [
          ...(arrival.length ? ["Arrival"] : []),
          ...(departure.length ? ["Departure"] : []),
        ];
        const nextDirection = options.includes(preferredDirection) ? preferredDirection : options[0] || "Arrival";
        setFlightSchedules({ Arrival: arrival, Departure: departure });
        setFlightDirection(nextDirection);
        setFlightDateError(options.length ? "" : "No Heathrow flights found in the recent/past day and next few days.");
        if (resetSelection) {
          setFlightDate("");
          setFlightAnchorUtc("");
        }
      } catch {
        if (requestId !== flightScheduleRequestSeq.current) return;
        setFlightSchedules({ Arrival: [], Departure: [] });
        setFlightDates([]);
        setFlightDateError("Could not load Heathrow flight times right now.");
        if (resetSelection) {
          setFlightDate("");
          setFlightAnchorUtc("");
        }
      }
    };

    if (debounce) flightScheduleTimer.current = setTimeout(runner, 180);
    else runner();
  };

  const selectFlightSuggestion = value => {
    const val = flightNumKey(value);
    setFlightNumber(val);
    setFlightSuggestions([]);
    setFlightActiveIdx(-1);
    setFlightFocused(false);
    setFlightDate("");
    setFlightAnchorUtc("");
    refreshFlightSchedules(val, { debounce: false, resetSelection: true });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError("");
    setResult(null);

    if (!pickupPostcode.trim() || !flightNumKey(flightNumber)) {
      setError("Please enter both your pickup postcode and flight number.");
      return;
    }

    if (!flightDate || !flightAnchorUtc) {
      setError("Select the exact Heathrow arrival or departure time before calculating.");
      return;
    }

    setLoading(true);
    try {
        const res = await api.post("/api/leave-time", {
          pickup_postcode: pickupPostcode.trim(),
          flight_number: flightNumKey(flightNumber),
          direction: resolvedFlightDirection,
          flight_date: flightDate,
          anchor_utc: flightAnchorUtc,
        passengers: Number(passengers) || 1,
        bags: Number(bags) || 0,
        has_checked_bags: hasCheckedBags,
        online_checkin: onlineCheckin,
        transport_mode: transportMode,
        risk_profile: riskProfile
      });
      setResult(res.data);
    } catch (err) {
      console.error("❌ leave-time error:", err.response?.data || err.message);
      setError(
        err.response?.data?.details ||
          err.response?.data?.error ||
          "We couldn't calculate a leave time. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const formatNice = iso => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-GB", {
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return iso;
    }
  };

  useEffect(() => {
    refreshFlightSchedules(flightNumber, { debounce: false, resetSelection: true });
    return () => {
      if (flightTimer.current) clearTimeout(flightTimer.current);
      if (flightScheduleTimer.current) clearTimeout(flightScheduleTimer.current);
    };
  }, []);

  useEffect(() => {
    const merged = [
      ...(flightSchedules.Arrival || []).map(s => ({ ...s, direction: "Arrival" })),
      ...(flightSchedules.Departure || []).map(s => ({ ...s, direction: "Departure" })),
    ].filter(s => s.direction === resolvedFlightDirection);

    const nextDates = merged.sort(
      (a, b) => new Date(scheduleAnchorTime(a)).getTime() - new Date(scheduleAnchorTime(b)).getTime()
    );
    setFlightDates(nextDates);

    if (!flightDate && nextDates.length === 1) {
      const onlyOption = nextDates[0];
      setFlightDirection(onlyOption.direction || resolvedFlightDirection);
      setFlightDate(scheduleDateKey(onlyOption) || "");
      setFlightAnchorUtc(String(scheduleAnchorTime(onlyOption) || ""));
    }

    if (flightDate && nextDates.length) {
      const hasSelection = nextDates.some(s => {
        const optionDate = scheduleDateKey(s);
        const optionAnchor = String(scheduleAnchorTime(s) || "");
        return String(optionDate) === String(flightDate) && String(optionAnchor) === String(flightAnchorUtc || "");
      });
      if (!hasSelection) {
        setFlightDate("");
        setFlightAnchorUtc("");
      }
    }
  }, [flightSchedules, resolvedFlightDirection, flightDate, flightAnchorUtc]);

  const handleFlightKeyDown = e => {
    if (!flightSuggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFlightActiveIdx(prev => (prev + 1 > flightSuggestions.length - 1 ? 0 : prev + 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setFlightActiveIdx(prev => (prev - 1 < 0 ? flightSuggestions.length - 1 : prev - 1));
      return;
    }
    if (e.key === "Enter" && flightActiveIdx >= 0) {
      e.preventDefault();
      const candidate = flightSuggestions[flightActiveIdx]?.flightNumber;
      if (candidate) selectFlightSuggestion(candidate);
    }
  };

  return (
    <>
      <Helmet>
        <title>When Should I Leave for Heathrow? | Smart Timing & Arrival Buffer Calculator</title>

        <meta
          name="description"
          content="Work out what time to leave for Heathrow. See how early to arrive, terminal buffer times, taxi vs self-park timing, and live-style estimates by flight number and postcode."
        />
        <meta
          name="keywords"
          content="when to leave for Heathrow, how early should I get to Heathrow, Heathrow departure time calculator, Heathrow arrival buffer time, Heathrow terminal timing guide, Heathrow security wait times, Heathrow from London travel time"
        />
        <link
          rel="canonical"
          href="https://www.cheapcabgrab.com/leave-for-heathrow"
        />

        <meta
          property="og:title"
          content="When Should I Leave for Heathrow?"
        />
        <meta
          property="og:description"
          content="Smart Heathrow leave-time calculator using your postcode, flight number, passengers and luggage."
        />
        <meta
          property="og:url"
          content="https://www.cheapcabgrab.com/leave-for-heathrow"
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "CheapCabGrab Heathrow Timing Calculator",
            operatingSystem: "All",
            applicationCategory: "TravelApplication",
            softwareVersion: "2026.1.14",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "GBP"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Heathrow Leave-Time Calculator",
            url: "https://www.cheapcabgrab.com/leave-for-heathrow",
            description:
              "Calculate when you should leave home for your Heathrow flight, based on your flight, luggage, passengers and travel mode.",
            about: [
              {
                "@type": "Service",
                name: "Heathrow Taxi",
                url: "https://www.cheapcabgrab.com/heathrow-taxi"
              },
              {
                "@type": "Service",
                name: "Airport Transfers",
                url: "https://www.cheapcabgrab.com"
              }
            ],
            mainEntity: {
              "@type": "FAQPage",
              mainEntity: [
                {
                  "@type": "Question",
                  name: "How early should I get to Heathrow?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Most international flights need 2–2.5 hours before departure; this tool factors your terminal, luggage, and security queues to give a precise arrival buffer."
                  }
                },
                {
                  "@type": "Question",
                  name: "When to leave for Heathrow from London?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Enter your postcode and flight number to see a live-style estimate including road distance, taxi/self-park travel time, and recommended leave time."
                  }
                },
                {
                  "@type": "Question",
                  name: "How long do Heathrow security and immigration take?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "We include typical Heathrow queue snapshots per terminal for security (departures) and immigration (arrivals), adjusting your recommended arrival buffer accordingly."
                  }
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <div className="parking-wrapper">
        <div className="parking-header">
          <h1>When Should I Leave for Heathrow?</h1>
          <p>
            Enter your postcode and choose the exact Heathrow flight time to get a smarter leave-time estimate based on terminal, queues and travel mode.
          </p>
          <p style={{ fontSize: 12, color: "#475467" }}>Choose the exact Heathrow arrival or departure time. Today, recent past flights and the next few days are included.</p>
          <p style={{ fontSize: 12, color: "#475467" }}>Updated: {todayUpdatedLabel}</p>
        </div>
       {/*
        <p className="parking-disclaimer" style={{ marginTop: 6 }}>
          These timings are estimates only, based on recent Heathrow flight data, distance from your postcode and
          typical airport behaviour. Live traffic, queues and airline rules can change at short notice. Always follow
          your airline’s official guidance and allow extra time if you prefer to travel stress-free.
        </p>
 */}

        {/* ================= FORM ================= */}
        <div className="parking-form">
          {error && (
            <div
              style={{
                marginBottom: 12,
                padding: "10px 12px",
                background: "#fef2f2",
                borderRadius: 8,
                fontSize: 14
              }}
            >
              ⚠️ {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Pickup Postcode</label>
              <input
                className="parking-input"
                type="text"
                value={pickupPostcode}
                onChange={e => setPickupPostcode(e.target.value)}
                placeholder="e.g. SL1 2LL"
              />
            </div>

            <div className="form-group">
              <label>Flight Number</label>
              <input
                className="parking-input"
                type="text"
                value={flightNumber}
                onChange={e => {
                  const val = e.target.value.toUpperCase();
                  setFlightNumber(val);
                  setResult(null);
                  setError("");
                  setFlightDate("");
                  setFlightAnchorUtc("");
                  fetchFlightSuggestions(val);
                  refreshFlightSchedules(val, { debounce: true, resetSelection: true });
                }}
                onKeyDown={handleFlightKeyDown}
                onFocus={() => setFlightFocused(true)}
                onBlur={() =>
                  setTimeout(() => {
                    setFlightFocused(false);
                    setFlightActiveIdx(-1);
                  }, 200)
                }
                placeholder="e.g. BA566"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={flightFocused && flightSuggestions.length > 0}
                aria-controls={flightListboxId}
                aria-activedescendant={flightFocused && flightActiveIdx >= 0 ? `leave-flight-option-${flightActiveIdx}` : undefined}
              />
              {flightFocused && flightSuggestions.length > 0 && (
                <div className="suggestions" id={flightListboxId} role="listbox" aria-label="Flight number suggestions">
                  {flightSuggestions.map((s, idx) => (
                    <button
                      type="button"
                      key={s.flightNumber}
                      className="suggestion"
                      id={`leave-flight-option-${idx}`}
                      role="option"
                      aria-selected={idx === flightActiveIdx}
                      style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: 0, cursor: "pointer" }}
                      onMouseEnter={() => setFlightActiveIdx(idx)}
                      onMouseDown={e => {
                        e.preventDefault();
                        selectFlightSuggestion(s.flightNumber);
                      }}
                    >
                      {s.flightNumber}
                    </button>
                  ))}
                </div>
              )}
              {flightError && <p style={{ color: "#b42318", marginTop: 8, marginBottom: 0 }}>{flightError}</p>}
            </div>

            {shouldShowFlightDirection && (
              <div className="form-group">
                <label>Flight Direction</label>
                <select
                  className="parking-input"
                  value={resolvedFlightDirection}
                  onChange={e => {
                    const nextDirection = String(e.target.value || "Arrival");
                    setFlightDirection(nextDirection);
                    setFlightDate("");
                    setFlightAnchorUtc("");
                    setResult(null);
                    setError("");
                  }}
                >
                  {availableFlightDirections.includes("Arrival") && <option value="Arrival">Arrives at Heathrow</option>}
                  {availableFlightDirections.includes("Departure") && <option value="Departure">Departs Heathrow</option>}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Flight Date / Time</label>
              <select
                className="parking-input"
                value={flightDate ? `${resolvedFlightDirection}|${flightDate}|${flightAnchorUtc || ""}` : ""}
                onChange={e => {
                  const [dir, val, anchor] = String(e.target.value || "").split("|");
                  setFlightDirection(dir || resolvedFlightDirection);
                  setFlightDate(val || "");
                  setFlightAnchorUtc(anchor || "");
                  setResult(null);
                  setError("");
                  setFlightDateError("");
                }}
              >
                <option value="">Select exact Heathrow flight time</option>
                {flightDates.map(s => {
                  const d = scheduleDateKey(s) || s.date || s.snapshot_date || "";
                  const anchorIso = scheduleAnchorTime(s);
                  const timeLabel = anchorIso ? formatScheduleLabel(anchorIso) : d || "—";
                  const labelPrefix = s.direction === "Arrival" ? "Arrives at Heathrow — " : "Departs Heathrow — ";
                  const terminalLabel = s.terminal ? ` • T${s.terminal}` : "";
                  const statusLabel = s.status ? ` • ${String(s.status)}` : "";
                  return (
                    <option
                      key={`${flightNumKey(flightNumber)}-${s.direction}-${d}-${anchorIso || ""}`}
                      value={`${s.direction}|${String(d).slice(0, 10)}|${anchorIso || ""}`}
                    >
                      {`${labelPrefix}${timeLabel}${terminalLabel}${statusLabel}`}
                    </option>
                  );
                })}
              </select>
              {flightDateError && <p style={{ color: "#b42318", marginTop: 8, marginBottom: 0 }}>{flightDateError}</p>}
            </div>

            <details className="leave-section-card leave-collapsible" style={{ marginTop: 12 }}>
              <summary className="leave-summary-bar">More options</summary>
              <div className="leave-collapsible-body">
                <div className="form-group">
                  <label>Passengers</label>
                  <select className="parking-input" value={passengers} onChange={e => setPassengers(e.target.value)}>
                    {[1, 2, 3, 4, 5, 6].map(n => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Checked Bags</label>
                  <select className="parking-input" value={bags} onChange={e => setBags(e.target.value)}>
                    {[0, 1, 2, 3, 4, 5, 6].map(n => (
                      <option key={n} value={n}>
                        {n} bag{n === 1 ? "" : "s"}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Check-in &amp; luggage</label>
                  <div className="option-list">
                    <label className="option-pill">
                      <input type="checkbox" checked={hasCheckedBags} onChange={e => setHasCheckedBags(e.target.checked)} />
                      <div className="option-text">
                        <span className="option-title">I need to check in bags at the airport</span>
                        <span className="option-sub">Choose this if you’ll be dropping bags at the check-in desk.</span>
                      </div>
                    </label>

                    <label className="option-pill">
                      <input type="checkbox" checked={onlineCheckin} onChange={e => setOnlineCheckin(e.target.checked)} />
                      <div className="option-text">
                        <span className="option-title">Online check-in will be done before arrival</span>
                        <span className="option-sub">Choose this if you’ll have your boarding pass on your phone or printed.</span>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>How are you travelling?</label>
                  <div className="option-grid option-grid-3">
                    <label className="option-card">
                      <input type="radio" name="transportMode" value="taxi" checked={transportMode === "taxi"} onChange={e => setTransportMode(e.target.value)} />
                      <div className="option-text">
                        <span className="option-title">Taxi / Private Hire</span>
                        <span className="option-sub">Door-to-door drop-off at the terminal.</span>
                      </div>
                    </label>

                    <label className="option-card">
                      <input type="radio" name="transportMode" value="self-park" checked={transportMode === "self-park"} onChange={e => setTransportMode(e.target.value)} />
                      <div className="option-text">
                        <span className="option-title">Drive &amp; self-park</span>
                        <span className="option-sub">Includes time for parking, shuttle bus and walking to the terminal.</span>
                      </div>
                    </label>

                    <label className="option-card">
                      <input type="radio" name="transportMode" value="train" checked={transportMode === "train"} onChange={e => setTransportMode(e.target.value)} />
                      <div className="option-text">
                        <span className="option-title">Train</span>
                        <span className="option-sub">Uses TfL journey time and falls back to road if unavailable.</span>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>How cautious do you want to be?</label>
                  <div className="option-grid option-grid-3">
                    <label className="option-card">
                      <input type="radio" name="riskProfile" value="business_pro" checked={riskProfile === "business_pro"} onChange={e => setRiskProfile(e.target.value)} />
                      <div className="option-text">
                        <span className="option-title">Business Pro</span>
                        <span className="option-sub">Tightest timing, e-Gate only, hand luggage.</span>
                      </div>
                    </label>

                    <label className="option-card">
                      <input type="radio" name="riskProfile" value="family_comfort" checked={riskProfile === "family_comfort"} onChange={e => setRiskProfile(e.target.value)} />
                      <div className="option-text">
                        <span className="option-title">Family Comfort</span>
                        <span className="option-sub">Adds extra time for bathroom breaks and airport friction.</span>
                      </div>
                    </label>

                    <label className="option-card">
                      <input type="radio" name="riskProfile" value="anxiety_free" checked={riskProfile === "anxiety_free"} onChange={e => setRiskProfile(e.target.value)} />
                      <div className="option-text">
                        <span className="option-title">Anxiety-Free</span>
                        <span className="option-sub">Largest buffer for long-haul or low-stress travel.</span>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </details>

<button className="parking-btn" type="submit" disabled={loading || !!flightError || !flightDate || !flightAnchorUtc}>
  {loading ? "Calculating…" : "Calculate Leave Time"}
</button>

</form> </div>
        {/* ================= RESULTS ================= */}
      {result && result.mode === "LEAVE_TIME_CALCULATED" && (
        <div className="parking-results">
          {result.journey.train_unavailable && (
            <div className="parking-disclaimer" style={{ background: "#fff7ed", border: "1px solid #fdba74", color: "#7c2d12" }}>
              Train route unavailable from TfL for this time/route; showing road timing instead.
            </div>
          )}
            {/* ✅ Hero result */}
            {(() => {
              const isArrival = result.scenario === "ARRIVAL_PICKUP";
              const readyWindow = result.timings.arrival_ready_window;
              return (
                <div className="leave-hero-card">
                  <div className="leave-hero-title">Recommended leave time</div>
                  <div className="leave-hero-time">
                    {formatNice(result.timings.leave_home_time_iso)}
                  </div>
                  {isArrival ? (
                    <div className="leave-hero-sub">
                      Meet at arrivals around {formatNice(result.timings.arrival_meet_time_iso)}. Passenger likely ready between{" "}
                      {readyWindow?.min_display || "—"} and {readyWindow?.max_display || "—"}.
                    </div>
                  ) : (
                    <div className="leave-hero-sub">
                      Arrive at Heathrow by {formatNice(result.timings.arrive_by_time_iso)} (includes security time).
                    </div>
                  )}
                  <div className="leave-hero-meta">
                    Based on flight {result.flight.iata_flight_identifier},{" "}
                    {result.flight.airline_name || "Airline"}, Terminal{" "}
                    {result.flight.terminal || "TBC"}, {result.journey.distance_source || "OSRM road"}
                  </div>
                </div>
              );
            })()}

            {/* ✅ Timing breakdown */}
            <div className="leave-section-card">
              <h3 className="leave-section-title">Timing breakdown</h3>
              <div className="leave-chips leave-chips-grid">
                {result.journey.transport_mode === "train" ? (
                  <>
                    <div className="leave-chip">
                      <div className="leave-chip-label">🚆 Train journey</div>
                      <div className="leave-chip-value">
                        {result.journey.train_journey?.duration_minutes
                          ? `${result.journey.train_journey.duration_minutes} mins`
                          : "—"}
                      </div>
                      <div className="leave-chip-sub">TfL journey time (includes walking and changes).</div>
                    </div>
                    <div className="leave-chip">
                      <div className="leave-chip-label">🛡️ Reliability buffer</div>
                      <div className="leave-chip-value">
                        ~{result.journey.train_buffers?.total_buffer_minutes || 0} mins
                      </div>
                      <div className="leave-chip-sub">
                        {result.journey.train_buffers
                          ? `Missed train ${result.journey.train_buffers.missed_train_buffer_minutes}m • terminal risk ${result.journey.train_buffers.terminal_risk_minutes}m • luggage ${result.journey.train_buffers.luggage_buffer_minutes}m • changes ${result.journey.train_buffers.change_penalty_minutes}m`
                          : "Protection for missed services, terminal walking, luggage."}
                      </div>
                      <div className="leave-chip-sub" style={{ color: "#0f172a" }}>
                        If everything runs perfectly, you may arrive early — this buffer avoids last-minute stress.
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="leave-chip">
                    <div className="leave-chip-label">🚗 Travel time</div>
                    <div className="leave-chip-value">
                      ~{result.timings.buffers.total_travel_minutes} mins
                    </div>
                    <div className="leave-chip-sub">
                      {result.journey.transport_mode === "self-park"
                        ? "road + parking shuttle"
                        : "road distance"}
                    </div>
                  </div>
                )}
                <div className="leave-chip">
                  <div className="leave-chip-label">🏢 Airport buffer</div>
                  <div className="leave-chip-value">
                    {result.timings.buffers.airport_buffer_minutes} mins
                  </div>
                  <div className="leave-chip-sub">
                    {(result.flight.terminal && `Terminal ${result.flight.terminal}, `) || ""}
                    {result.scenario === "DEPARTURE"
                      ? `departure buffer incl. security ~${result.timings.buffers.security_wait_minutes || "—"} mins`
                      : `arrival buffer incl. immigration ~${result.timings.buffers.immigration_wait_minutes || "—"} mins`}
                  </div>
                </div>
                {result.scenario === "ARRIVAL_PICKUP" && (
                  <div className="leave-chip">
                    <div className="leave-chip-label">🛬 Arrival ready window</div>
                    <div className="leave-chip-value">
                      {(result.timings.arrival_ready_window?.min_display || "—") +
                        " – " +
                        (result.timings.arrival_ready_window?.max_display || "—")}
                    </div>
                    <div className="leave-chip-sub">
                      Immigration + baggage, based on terminal snapshots.
                    </div>
                  </div>
                )}
                  <div className="leave-chip">
                    <div className="leave-chip-label">📍 Distance</div>
                    <div className="leave-chip-value">
                      {result.journey.distance_miles} miles · {result.journey.distance_km} km
                    </div>
                    <div className="leave-chip-sub">
                      {result.journey.distance_source || "OSRM road"}
                    </div>
                  </div>
                {result.journey.transport_mode === "train" && (
                  <div className="leave-chip">
                    <div className="leave-chip-label">🚆 Train time</div>
                    <div className="leave-chip-value">
                      {result.journey.train_journey?.duration_minutes
                        ? `${result.journey.train_journey.duration_minutes} mins`
                        : "—"}
                    </div>
                    <div className="leave-chip-sub">
                      {result.journey.train_buffers
                        ? `Buffers: station ${result.journey.train_buffers.station_arrival_minutes}m, platform ${result.journey.train_buffers.platform_boarding_minutes}m, changes ${result.journey.train_buffers.change_penalty_minutes}m, terminal ${result.journey.train_buffers.terminal_walk_minutes}m, missed train ${result.journey.train_buffers.missed_train_buffer_minutes}m`
                        : result.journey.train_journey
                        ? `${result.journey.train_journey.interchanges || 0} changes, walk ${result.journey.train_journey.total_walk_minutes || 0} mins`
                        : "TfL route used when available"}
                    </div>
                    {result.journey.transport_mode === "train" && (
                      <div className="leave-chip-sub" style={{ color: "#0f172a", fontWeight: 600 }}>
                        Train leave time includes these buffers; missing a train adds 15–30 mins.
                      </div>
                    )}
                  </div>
                )}
                {result.journey.train_unavailable && (
                  <div className="leave-chip" style={{ borderColor: "#f59e0b", background: "#fffbeb" }}>
                    <div className="leave-chip-label">⚠️ Train unavailable</div>
                    <div className="leave-chip-sub">
                      TfL journey not found for this time/route; using road timing instead.
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ✅ Flight summary (collapsible) */}
            <details className="leave-section-card leave-collapsible">
              <summary className="leave-summary-bar">
                ✈️ Flight details — {result.flight.iata_flight_identifier} ({result.flight.airline_name || "Airline"}, T{result.flight.terminal || "BC"})
              </summary>
              <div className="leave-flight-table leave-collapsible-body">
                <div className="leave-flight-row">
                  <span className="leave-flight-label">Type</span>
                  <span className="leave-flight-value">
                    {result.scenario === "DEPARTURE" ? "Departure from Heathrow" : "Arrival into Heathrow"}
                  </span>
                </div>
                <div className="leave-flight-row">
                  <span className="leave-flight-label">From</span>
                  <span className="leave-flight-value">
                    {result.flight.origin_city} ({result.flight.origin_airport_iata})
                  </span>
                </div>
                <div className="leave-flight-row">
                  <span className="leave-flight-label">To</span>
                  <span className="leave-flight-value">
                    {result.flight.destination_city} ({result.flight.destination_airport_iata})
                  </span>
                </div>
                <div className="leave-flight-row">
                  <span className="leave-flight-label">Terminal</span>
                  <span className="leave-flight-value">
                    {result.flight.terminal ? `Terminal ${result.flight.terminal}` : "TBC"}
                  </span>
                </div>
                <div className="leave-flight-row">
                  <span className="leave-flight-label">Scheduled</span>
                  <span className="leave-flight-value">
                    {formatNice(result.timings.scheduled_time_iso)}
                  </span>
                </div>
                <div className="leave-flight-row">
                  <span className="leave-flight-label">Live status</span>
                  <span className="leave-flight-value">
                    {result.flight.status_message || "Check with airline / Heathrow"}
                  </span>
                </div>
                {result.flight?.checkin_start_local && (
                  <div className="leave-flight-row">
                    <span className="leave-flight-label">Check-in opens</span>
                    <span className="leave-flight-value">
                      {new Date(result.flight.checkin_start_local).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                      {result.flight.checkin_zone && ` in Zone ${result.flight.checkin_zone}`}
                    </span>
                  </div>
                )}
              </div>
            </details>

            {/* ✅ Explanation block (collapsible) */}
            <details className="leave-section-card leave-collapsible">
              <summary className="leave-summary-bar">🧠 How we worked this out</summary>
              <div className="leave-explainer leave-explainer-grid leave-collapsible-body">
                <div className="leave-grid">
                  <div className="leave-grid-row">
                    <span className="leave-grid-label">Flight</span>
                    <span className="leave-grid-value">
                      {result.flight.iata_flight_identifier} ({result.flight.airline_name || "Airline"})
                    </span>
                  </div>
                  <div className="leave-grid-row">
                    <span className="leave-grid-label">Flight type</span>
                    <span className="leave-grid-value">
                      {result.flight.route_type === "D"
                        ? "Domestic"
                        : "International / European"}
                    </span>
                  </div>
                  <div className="leave-grid-row">
                    <span className="leave-grid-label">Scheduled time</span>
                    <span className="leave-grid-value">{formatNice(result.timings.scheduled_time_iso)}</span>
                  </div>
                  {result.arrival_or_departure === "D" && (
                    <div className="leave-grid-row">
                      <span className="leave-grid-label">Check-in / zone</span>
                      <span className="leave-grid-value">
                        {result.flight.checkin_start_local
                          ? new Date(result.flight.checkin_start_local).toLocaleString()
                          : "—"}
                        {result.flight.checkin_zone && ` (Zone ${result.flight.checkin_zone})`}
                      </span>
                    </div>
                  )}
                  {result.arrival_or_departure === "D" && result.flight.gate_number && (
                    <div className="leave-grid-row">
                      <span className="leave-grid-label">Gate</span>
                      <span className="leave-grid-value">{result.flight.gate_number}</span>
                    </div>
                  )}
                  {result.scenario === "DEPARTURE" && (
                    <div className="leave-grid-row">
                      <span className="leave-grid-label">Arrive at Heathrow by</span>
                      <span className="leave-grid-value">
                        {formatNice(result.timings.arrive_by_time_iso)}
                      </span>
                    </div>
                  )}
                  {result.scenario === "ARRIVAL_PICKUP" && (
                    <>
                      <div className="leave-grid-row">
                        <span className="leave-grid-label">Meet at arrivals</span>
                        <span className="leave-grid-value">
                          {formatNice(result.timings.arrival_meet_time_iso)}
                        </span>
                      </div>
                      <div className="leave-grid-row">
                        <span className="leave-grid-label">Passenger ready window</span>
                        <span className="leave-grid-value">
                          {(result.timings.arrival_ready_window?.min_display || "—") +
                            " – " +
                            (result.timings.arrival_ready_window?.max_display || "—")}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="leave-grid-row">
                    <span className="leave-grid-label">Leave home at</span>
                    <span className="leave-grid-value">
                      {formatNice(result.timings.leave_home_time_iso)}
                    </span>
                  </div>
                  <div className="leave-grid-row">
                    <span className="leave-grid-label">Your inputs</span>
                    <span className="leave-grid-value">
                      {(result.journey.passengers || result.input?.passengers || passengers)} passengers · {result.input?.bags ?? bags} bags ·{" "}
                      {result.journey.transport_mode === "train"
                        ? "Train"
                        : result.journey.transport_mode === "self-park"
                        ? "Drive & self-park"
                        : "Taxi / private hire"}
                    </span>
                  </div>
                  <div className="leave-grid-row">
                    <span className="leave-grid-label">Travel time</span>
                    <span className="leave-grid-value">
                  {result.journey.transport_mode === "train"
                        ? `${(result.journey.train_journey?.duration_minutes || 0) + (result.journey.train_buffers?.total_buffer_minutes || 0)} min train (incl reliability buffer)`
                        : result.timings.buffers?.drive_minutes != null
                        ? `${result.timings.buffers.drive_minutes} min road`
                        : "—"}{" "}
                  {result.journey.transport_mode === "train"
                    ? ""
                        : `for ~${result.journey.distance_miles} miles (${result.journey.distance_km} km)`}
                    </span>
                  </div>
                  <div className="leave-grid-row">
                    <span className="leave-grid-label">Final recommendation</span>
                    <span className="leave-grid-value">{result.recommendation.summary}</span>
                  </div>
                </div>

                <p style={{ marginTop: 10 }}>
                  This recommendation uses your flight terminal, scheduled time, estimated road distance from your
                  postcode, and typical Heathrow queue times. For departures we include security; for arrivals we include
                  immigration and baggage. We also factor in your luggage, passengers and whether you’re driving/parking or using a taxi.
                </p>
                <ul>
                  {result.recommendation.notes?.map((note, idx) => (
                    <li key={idx}>{note}</li>
                  ))}
                </ul>
              </div>
            </details>

            {/* ✅ Factors section */}
            <details className="leave-section-card leave-collapsible">
              <summary className="leave-summary-bar">⚠️ Factors that can change this</summary>
              <ul className="leave-explainer leave-explainer-grid leave-collapsible-body" style={{ marginBottom: 0 }}>
                <li>Traffic incidents near Heathrow or on your route</li>
                <li>Terminal congestion or long security lines</li>
                <li>Airline check-in desk opening times</li>
                <li>Weather or security alerts</li>
              </ul>
            </details>

            {/* ✅ Disclaimer + internal links */}
            <p className="parking-disclaimer" style={{ marginTop: 16 }}>
              {result.disclaimer}
            </p>

            <div style={{ marginTop: 18, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#f9fafb" }}>
              <strong>Useful next steps</strong>
              <ul style={{ marginTop: 8, lineHeight: 1.6, marginBottom: 0 }}>
                {result.journey.transport_mode === "train" && (
                  <li>🚆 <a href="/train-vs-taxi-elizabeth-line">Elizabeth Line vs Taxi stress comparison</a></li>
                )}
                {result.journey.transport_mode === "self-park" && (
                  <li>🅿️ <a href="/heathrow-parking-vs-taxi">Heathrow Parking vs Taxi Cost Calculator</a></li>
                )}
                {result.scenario === "ARRIVAL_PICKUP" && (
                  <li>🅿️ <a href="/heathrow-dropoff-free">Heathrow free drop-off &amp; pick-up guide</a></li>
                )}
                <li>🚖 <a href="https://www.cheapcabgrab.com/heathrow-taxi">Book a Heathrow taxi</a></li>
                <li>🚇 <a href="https://tfl.gov.uk/" target="_blank" rel="noopener noreferrer">Check TfL trains</a></li>
                {result.journey.transport_mode === "self-park" && (
                  <li>🅿️ <a href="https://www.heathrow.com/transport-and-directions/heathrow-parking" target="_blank" rel="noopener noreferrer">Official Heathrow parking</a></li>
                )}
              </ul>
            </div>

            <details className="leave-section-card leave-collapsible" open>
              <summary className="leave-summary-bar">❓ FAQs</summary>
              <ul className="leave-explainer leave-explainer-grid leave-collapsible-body" style={{ marginBottom: 0 }}>
                <li><strong>Is 2 hours enough for a British Airways flight in 2026?</strong> For European flights, yes. For long-haul, plan 3 hours because T5 “B” gates require a shuttle train adding ~20 minutes.</li>
                <li><strong>Can I drop off an elderly passenger for free?</strong> The £7 fee applies to all; Blue Badge holders can apply for a 100% discount. Our Meet &amp; Greet taxi avoids the 10-minute drop-off limit.</li>
                <li><strong>What is the Terminal 5 “35-minute” rule?</strong> British Airways passengers must clear security at least 35 minutes before departure, or the system may offload them.</li>
                <li><strong>Has the drop-off charge changed?</strong> Yes. From 1 Jan 2026 the drop-off charge is £7 per visit with a strict 10-minute maximum stay.</li>
                <li><strong>Does Terminal 4 need extra time on trains?</strong> Yes. Add 20–30 minutes for the Heathrow Central transfer if you’re using Elizabeth Line or HEX to reach T4.</li>
              </ul>
            </details>

            <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fff" }}>
              <strong>Plan your Heathrow journey:</strong>
              <ul style={{ marginTop: 8, lineHeight: 1.6, marginBottom: 0 }}>
                <li>🚖 <a href="/heathrow-fare-compare">Compare taxi fares</a></li>
                <li>🚆 <a href="/train-vs-taxi">Train vs taxi</a></li>
                <li>🅿️ <a href="/heathrow-parking-vs-taxi">Parking vs taxi</a></li>
                <li>✈️ <a href="/flight-checker">Check your flight</a></li>
                <li>🕒 <a href="/leave-for-heathrow">When to leave for Heathrow</a></li>
                <li>🧭 <a href="/terminalFinder">Find your terminal</a></li>
                <li>🧳 <a href="/arrival-assistant">Arrival assistant</a></li>
              </ul>
            </div>
          </div>
        )}

        <div style={{ marginTop: 40 }}>
          <ReviewsBar />
        </div>
      </div>
    </>
  );
};

export default LeaveForHeathrow;
